import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useOrganization } from '@/hooks/useOrganization';

interface DemoModeContextType {
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  canAccessDemoMode: boolean;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

const DEMO_MODE_STORAGE_KEY = 'poco-demo-mode';

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const { hasCapability } = useOrganization();
  const [isDemoMode, setIsDemoMode] = useState(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(DEMO_MODE_STORAGE_KEY);
      return stored === 'true';
    }
    return false;
  });

  // Check if user has manager+ role
  const canAccessDemoMode = hasCapability('canAccessDemoMode');

  // If user doesn't have access, ensure demo mode is off
  useEffect(() => {
    if (!canAccessDemoMode && isDemoMode) {
      setIsDemoMode(false);
      localStorage.removeItem(DEMO_MODE_STORAGE_KEY);
    }
  }, [canAccessDemoMode, isDemoMode]);

  // Sync to localStorage
  useEffect(() => {
    if (isDemoMode) {
      localStorage.setItem(DEMO_MODE_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(DEMO_MODE_STORAGE_KEY);
    }
  }, [isDemoMode]);

  // Set data attribute on document for CSS targeting
  useEffect(() => {
    if (isDemoMode) {
      document.documentElement.setAttribute('data-demo-mode', 'true');
    } else {
      document.documentElement.removeAttribute('data-demo-mode');
    }
  }, [isDemoMode]);

  const toggleDemoMode = () => {
    if (canAccessDemoMode) {
      setIsDemoMode((prev) => !prev);
    }
  };

  return (
    <DemoModeContext.Provider value={{ isDemoMode, toggleDemoMode, canAccessDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoModeContext() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoModeContext must be used within a DemoModeProvider');
  }
  return context;
}
